import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../../store';
import {
  EntityListsSummary,
  EntityListTypeSupported,
  getEntityListSpecification,
} from '../../entityListsLib';
import React, { useCallback } from 'react';
import { setShowArchivedItems, setSortField } from '../../entityListAdministrationSlice';
import { addToast } from '@ydistri/utils';
import ShowArchivedItemsToggle from '../../../common/ShowArchivedItemsToggle';
import SortSelector from '../../../common/SortSelector';
import EntityListSourceTypeFilter from './EntityListSourceTypeFilter';
import AdministrationFiltersPanel from '../../../common/AdministrationFiltersPanel';

interface EntityListFiltersProps {
  entityListType: EntityListTypeSupported;
  entityListsSummary: EntityListsSummary;
}

const EntityListsFilters: React.FC<EntityListFiltersProps> = ({
  entityListType,
  entityListsSummary,
}) => {
  const dispatch = useAppDispatch();

  const showArchivedItems = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].showArchivedItems,
  );

  const sortField = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].sortField,
  );

  /**
   * Shows or hides archived items
   */
  const toggleShowArchived = useCallback(() => {
    const newShowArchivedItems = !showArchivedItems;
    dispatch(setShowArchivedItems({ entityListType, data: newShowArchivedItems }));
    if (newShowArchivedItems) {
      const entityListSpecification = getEntityListSpecification(entityListType);
      dispatch(
        addToast({
          message: `Showing archived ${entityListSpecification.title}s`,
          description: `Archived ${entityListSpecification.title}s are shown below the active ones`,
        }),
      );
    }
  }, [dispatch, entityListType, showArchivedItems]);

  const onSortChange = useCallback(
    (v: string) => {
      dispatch(setSortField({ entityListType, data: v }));
    },
    [dispatch, entityListType],
  );

  return (
    <AdministrationFiltersPanel>
      <ShowArchivedItemsToggle
        archivedItemsCount={entityListsSummary.archivedItemsCount}
        checked={showArchivedItems}
        onClick={toggleShowArchived}
      />
      <EntityListSourceTypeFilter
        entityListType={entityListType}
        entityListSourceTypeSummary={entityListsSummary.sourceTypeCounts}
      />
      <SortSelector onSortChange={onSortChange} defaultValue={sortField} />
    </AdministrationFiltersPanel>
  );
};

export default EntityListsFilters;
